import { z } from "zod";
import { ValidationError } from "../errors/ValidationError";
import { dispatchValidationError } from "../errors/errorDispatcher";

const maxAttachmentBytes = 2 * 1024 * 1024;
const allowedMimeTypes = ["image/jpeg", "image/png", "application/pdf"];
const ticketStateValue = z.enum(["aperto", "in-lavorazione", "chiuso"]);

export const MAX_TITLE_LEN = 255;
export const MAX_SUBJECT_LEN = MAX_TITLE_LEN;
export const MAX_DESCRIPTION_LEN = 1000;
export const MAX_MESSAGE_LEN = 1000;

const attachmentFileSchema = z
  .any()
  .optional()
  .nullable()
  .refine((file) => {
    if (!file) return true;
    const size = typeof file.size === "number" ? file.size : null;
    if (size == null) return false;
    if (size > maxAttachmentBytes) return false;
    const type = typeof file.type === "string" ? file.type : "";
    if (!type) return false;
    return allowedMimeTypes.includes(type);
  }, { message: "Allegato non valido (max 2MB, consentiti: JPEG/PNG/PDF)" });

export const ticketCreateRequestSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: "Oggetto obbligatorio" })
    .max(MAX_SUBJECT_LEN, { message: `L'oggetto non può superare i ${MAX_SUBJECT_LEN} caratteri` }),
  description: z
    .string()
    .trim()
    .min(1, { message: "Descrizione obbligatoria" })
    .max(MAX_DESCRIPTION_LEN, { message: `La descrizione non può superare i ${MAX_DESCRIPTION_LEN} caratteri` }),
  file: attachmentFileSchema,
});

export function validateTicketCreateRequest(data) {
  const parsed = ticketCreateRequestSchema.safeParse(data);
  if (!parsed.success) {
    const message = `${parsed.error.issues[0]?.message ?? "unknown error"}`;
    dispatchValidationError({ message, issues: parsed.error.issues, raw: parsed.error });
    throw new ValidationError(message, { issues: parsed.error.issues, raw: parsed.error });
  }
  return parsed.data;
}

export const ticketListRequestSchema = z.object({
  state: ticketStateValue.optional().nullable(),
  sortBy: z.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
  page: z.number().int().min(0).optional().default(0),
  size: z.number().int().min(1).max(200).optional().default(20),
});

export const ticketListItemSchema = z.object({
  id: z.string(),
  subject: z.string(),
  state: ticketStateValue,
  createdAt: z.any().optional().nullable(),
  updatedAt: z.any().optional().nullable(),
  unreadCount: z.coerce.number().optional().nullable(),
  description: z.string().optional().nullable(),
});

export const ticketListResponseSchema = z.object({
  items: z.array(ticketListItemSchema),
  totalCount: z.coerce.number(),
  page: z.coerce.number().int(),
  size: z.coerce.number().int(),
});

export const ticketCreateResponseSchema = z.object({
  ticketId: z.string(),
});

export const ticketMessageSendRequestSchema = z.object({
  ticketId: z.string().uuid(),
  message: z.string().trim().max(MAX_MESSAGE_LEN).optional().nullable(),
  file: attachmentFileSchema,
});

export const ticketMessageListRequestSchema = z.object({
  ticketId: z.string().uuid(),
  page: z.number().int().min(0).optional().default(0),
});

export const ticketMessageItemSchema = z.object({
  id: z.string(),
  operator: z.boolean(),
  message: z.string().optional().nullable(),
  attachmentName: z.string().optional().nullable(),
  timestamp: z.any().optional().nullable(),
  editable: z.boolean().optional().default(false),
  read: z.boolean().optional().default(false),
});

export const ticketMessageListResponseSchema = z.object({
  items: z.array(ticketMessageItemSchema),
  totalCount: z.coerce.number(),
  page: z.coerce.number().int(),
  size: z.coerce.number().int(),
});

export const ticketMessageSendResponseSchema = z.object({
  messageId: z.string(),
});

export const ticketSnapshotSchema = z.object({
  id: z.string(),
  state: ticketStateValue,
  updatedAt: z.any().optional().nullable(),
  unreadCount: z.coerce.number(),
});

export const ticketMessagePollRequestSchema = z.object({
  ticketId: z.string().uuid(),
  afterTimestamp: z.any().optional().nullable(),
  ownMessageIds: z.array(z.string()).optional().default([]),
});

export const ticketSummaryResponseSchema = z.object({
  menuBadgeCount: z.coerce.number(),
  pollingEnabled: z.boolean(),
  tickets: z.array(ticketSnapshotSchema).optional().default([]),
});

export const ticketMessagePollResponseSchema = z.object({
  items: z.array(ticketMessageItemSchema),
  readMessageIds: z.array(z.string()).optional().default([]),
});

export const ticketAttachmentRequestSchema = z.object({
  ticketId: z.string().uuid(),
  messageId: z.string().uuid(),
});

export const ticketAttachmentResponseSchema = z.object({
  attachmentName: z.string().optional().nullable(),
  attachmentBase64: z.string(),
});

export const ticketMessageEditRequestSchema = z.object({
  ticketId: z.string().uuid(),
  messageId: z.string().uuid(),
  action: z.enum(["edit", "delete"]),
  message: z.string().trim().max(MAX_MESSAGE_LEN).optional().nullable(),
  removeAttachment: z.boolean().optional(),
  file: z.any().optional().nullable(),
  existingAttachmentKept: z.boolean().optional(),
});

function throwValidation(message, error) {
  dispatchValidationError({ message, issues: error.issues, raw: error });
  throw new ValidationError(message, { issues: error.issues, raw: error });
}

export function validateTicketListRequest(data) {
  const parsed = ticketListRequestSchema.safeParse(data ?? {});
  if (!parsed.success) {
    throwValidation(`Invalid ticket list payload: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function parseTicketListResponse(data) {
  const parsed = ticketListResponseSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Unexpected ticket list response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function parseTicketCreateResponse(data) {
  const parsed = ticketCreateResponseSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Unexpected ticket create response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function validateTicketMessageSendRequest(data) {
  const parsed = ticketMessageSendRequestSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(parsed.error.issues[0]?.message ?? "Messaggio non valido", parsed.error);
  }
  const hasMessage = Boolean(parsed.data.message?.trim());
  const hasFile = Boolean(parsed.data.file);
  if (!hasMessage && !hasFile) {
    const message = "Inserisci un messaggio o un allegato";
    dispatchValidationError({ message });
    throw new ValidationError(message);
  }
  return parsed.data;
}

export function validateTicketMessageListRequest(data) {
  const parsed = ticketMessageListRequestSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Invalid ticket message list payload: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function parseTicketMessageListResponse(data) {
  const parsed = ticketMessageListResponseSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Unexpected ticket message list response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function parseTicketMessageSendResponse(data) {
  const parsed = ticketMessageSendResponseSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Unexpected ticket message response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function validateTicketAttachmentRequest(data) {
  const parsed = ticketAttachmentRequestSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Invalid ticket attachment payload: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function parseTicketAttachmentResponse(data) {
  const parsed = ticketAttachmentResponseSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Unexpected ticket attachment response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function validateTicketMessageEditRequest(data) {
  const parsed = ticketMessageEditRequestSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(parsed.error.issues[0]?.message ?? "Richiesta modifica non valida", parsed.error);
  }
  if (parsed.data.action === "edit") {
    const hasMessage = Boolean(parsed.data.message?.trim());
    const hasFile = Boolean(parsed.data.file);
    const keepingAttachment = Boolean(parsed.data.existingAttachmentKept);
    if (!hasMessage && !hasFile && !keepingAttachment) {
      const message = "Inserisci un messaggio o un allegato";
      dispatchValidationError({ message });
      throw new ValidationError(message);
    }
  }
  return parsed.data;
}

export function parseTicketSummaryResponse(data) {
  const parsed = ticketSummaryResponseSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Unexpected ticket summary response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function validateTicketMessagePollRequest(data) {
  const parsed = ticketMessagePollRequestSchema.safeParse(data);
  if (!parsed.success) {
    throwValidation(`Invalid ticket message poll payload: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}

export function parseTicketMessagePollResponse(data) {
  const parsed = ticketMessagePollResponseSchema.safeParse(data ?? { items: [] });
  if (!parsed.success) {
    throwValidation(`Unexpected ticket message poll response: ${parsed.error.message}`, parsed.error);
  }
  return parsed.data;
}
